import { DataGrid, GridColDef } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { fetchReviewsData, totalAvgCount } from "../../services/reviewsApi";
import { CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";

interface Props {
  isPrintMode: boolean;
  dateFilter: {
    startDate: string;
    endDate: string;
  };
  clinicData: string;
}

const getStars = (rating: number): JSX.Element[] => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 !== 0;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  const stars = [];

  // Add full stars
  for (let i = 0; i < fullStars; i++) {
    stars.push(
      <span key={`full-${i}`} className="text-yellow-400 text-2xl">
        ★
      </span>
    );
  }

  // Add half star if needed
  if (hasHalfStar) {
    stars.push(
      <span key="half" className="text-yellow-400 text-2xl">
        ★
      </span>
    );
  }

  // Add empty stars
  for (let i = 0; i < emptyStars; i++) {
    stars.push(
      <span key={`empty-${i}`} className="text-gray-400 text-2xl">
        ★
      </span>
    );
  }

  return stars;
};

const ReviewsSection = ({ isPrintMode, dateFilter, clinicData }: Props) => {
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState<any>();
  const [rows, setRows] = useState<any[]>([]);

  useEffect(() => {
    const fetchReviews = async () => {
      setLoading(true);
      try {
        const [avgCountResponse, reviewsResponse] = await Promise.all([
          totalAvgCount({
            clinicUniqueId: clinicData,
            startDate: dateFilter.startDate,
            endDate: dateFilter.endDate,
          }),
          fetchReviewsData({
            clinicUniqueId: clinicData,
            startDate: dateFilter.startDate,
            endDate: dateFilter.endDate,
          }),
        ]);

        // Update summary stats from Google data
        if (avgCountResponse?.status?.msg === "success") {
          setRating(avgCountResponse);
          // setAverageRating(`${googleData?.Avgrating || 4.853658536585366}/5`);
          // setTotalReviews(parseInt(googleData?.finalTotal) || 88);
          // setNewReviews(parseInt(googleData?.total) || 41);
        }

        // Update platform ratings
        if (reviewsResponse?.status?.msg === "success") {
          const platforms = Object.keys(reviewsResponse).filter(
            (key) => key !== "status"
          );
          const platformData = platforms
            .map((key) => reviewsResponse[key])
            .filter((item) => item.source);
          console.log("platformData", platformData);
          setRows(
            platformData
              .map((item: any, index: number) => {
                const rating = item.rating || {};
                const newReviews = parseInt(item.total) || 0;
                const totalReviews = parseInt(item.finalTotal) || 0;

                return {
                  id: index + 1,
                  source: item.source,
                  rating: `${newReviews}/${totalReviews}`,
                  Avgrating: item.Avgrating
                    ? `${parseFloat(item.Avgrating).toFixed(1)}/5`
                    : "0/5",
                  star1: rating["1"] || "0",
                  star2: rating["2"] || "0",
                  star3: rating["3"] || "0",
                  star4: rating["4"] || "0",
                  star5: rating["5"] || "0",
                };
              })
              .filter((row) => row.rating !== "0/0")
          ); // <== Add this line
        }
      } catch (error) {
        console.error("Error fetching reviews data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (clinicData && dateFilter?.startDate && dateFilter?.endDate) {
      fetchReviews();
    }
  }, [dateFilter, clinicData]);

  const LoadingSpinner = styled(CircularProgress)({
    color: "#194E5B",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  });

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md h-[600px] relative">
        <h2 className="text-2xl font-bold text-[#194E5B] mb-4">
          Reviews and Ratings
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="col-span-2">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  const columns: GridColDef[] = [
    {
      field: "source",
      headerName: "Platform",
      width: 200,
      disableColumnMenu: true,
      filterable: false,
      hideSortIcons: true,
      resizable: false,
      flex: 1,
    },
    {
      field: "rating",
      headerName: "New/Total Reviews",
      width: 200,
      disableColumnMenu: true,
      filterable: false,
      hideSortIcons: true,
      resizable: false,
      flex: 1,
    },
    {
      field: "Avgrating",
      headerName: "Ratings",
      width: 100,
      disableColumnMenu: true,
      filterable: false,
      hideSortIcons: true,
      resizable: false,
      flex: 1,
    },
    {
      field: "star1",
      headerName: "★",
      width: 50,
      disableColumnMenu: true,
      filterable: false,
      hideSortIcons: true,
      resizable: false,
      flex: 1,
    },
    {
      field: "star2",
      headerName: "★★",
      width: 80,
      disableColumnMenu: true,
      filterable: false,
      hideSortIcons: true,
      resizable: false,
      flex: 1,
    },
    {
      field: "star3",
      headerName: "★★★",
      width: 80,
      disableColumnMenu: true,
      filterable: false,
      hideSortIcons: true,
      resizable: false,
      flex: 1,
    },
    {
      field: "star4",
      headerName: "★★★★",
      width: 80,
      disableColumnMenu: true,
      filterable: false,
      hideSortIcons: true,
      resizable: false,
      flex: 1,
    },
    {
      field: "star5",
      headerName: "★★★★★",
      width: 80,
      disableColumnMenu: true,
      filterable: false,
      hideSortIcons: true,
      resizable: false,
      flex: 1,
    },
  ];

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold text-[#194E5B] mb-4">
        Reviews and Ratings
      </h2>
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-4 w-full">
          {/* Card 1 */}
          <div className="bg-[#194e5b] text-white rounded-lg p-6 shadow flex-1 flex flex-col items-center justify-center">
            <h3 className="text-lg font-medium mb-2">Average Rating</h3>
            <div className="flex items-center mb-2">
              {getStars(Number(rating?.all?.Avgrating))}
            </div>
            <p className="text-2xl font-bold">{Number(rating?.all?.Avgrating).toFixed(1)}/5</p>
          </div>

          {/* Card 2 */}
          <div className="bg-[#C9EEEB] text-[#2B5D69] rounded-lg p-6 shadow flex-1 flex flex-col items-center justify-center">
            <h3 className="text-lg font-medium mb-2">Total Reviews</h3>
            <div className="flex items-center mb-2">
              {/* Add any icons or additional content here */}
            </div>
            <p className="text-2xl font-bold">{rating?.all?.total}</p>
          </div>

          {/* Card 3 */}
          <div className="bg-[#194e5b] text-white rounded-lg p-6 shadow flex-1 flex flex-col items-center justify-center">
            <h3 className="text-lg font-medium mb-2">New Reviews</h3>
            <div className="flex items-center mb-2">
              {/* Add any icons or additional content here */}
            </div>
            <p className="text-2xl font-bold">{Number(rating?.total)}</p>
          </div>
        </div>
      </div>
      <h2 className="text-2xl font-bold text-[#194E5B] mb-4">
        Platform Ratings
      </h2>
      <div className="overflow-x-auto">
        <DataGrid
          rows={rows}
          columns={columns}
          autoHeight
          disableColumnMenu
          hideFooterPagination
        />
      </div>
    </div>
  );
};

export default ReviewsSection;
